import fs from 'fs';
import chalk from 'chalk';

// * fast-glob
import fastGlob from 'fast-glob';
const { globSync: fg } = fastGlob;

// * constants
const LF = '\n'; // * Line Feed
const SP = '    '; // * Space
const BR = '├── '; // * Branch
const BRE = '└── '; // * Branch End
const PBR = '│   '; // * Pre Branch

// * types
import type { Tree, Branches, TreeOptions, StringifyOptions } from '../types/index.js';

export const readDirectory = (root: string, ignore: string[]) => {
  if (!fs.existsSync(root)) throw new Error(`Could not find '${root}'`);
  if (!fs.statSync(root).isDirectory()) throw new Error(`'${root}' is not a directory`);

  const noramlizedRoot = root.replace(/\\/g, '/');
  const pattern = noramlizedRoot + '/**/*';
  const paths = fg(pattern, {
    onlyFiles: false,
    markDirectories: true,
    ignore: ['node_modules/**', ...ignore],
  });

  return paths;
};

export const createDirectoryTree = (root: string, options: TreeOptions = {}) => {
  const { rootAlias = '.', ignore = [] } = options;
  const paths = readDirectory(root, ignore);
  const tree: Tree = { name: rootAlias, dir: true, fullPath: root, branches: [] };

  for (const path of paths) {
    const pathChunks = path.replace(root, '').split('/').filter(Boolean);
    const isDir = path.endsWith('/');
    let subTree = tree.branches;

    for (let i = 0; i < pathChunks.length; i++) {
      const fullPath = ['.', ...pathChunks.slice(0, i + 1)].join('/');
      const chunk = pathChunks[i]!;
      let node = subTree.find(n => n.name === chunk);

      if (!node) {
        node = { name: chunk, dir: isDir, fullPath, branches: [] };
        subTree.push(node);
      }

      subTree = node.branches;
    }
  }

  return tree;
};

/**
 * * This function requires a major refactor or mabye it should
 * * be broken down into multiple functions
 */
export const stringifyBranches = (
  branches: Branches,
  activePath: string,
  selectedPaths: string[],
  level: number,
  parents: number[],
  // * [0, 1, 0, 0, 1] ==> previous parents
  // * if 1 parent is the last node of the level
  // * default is [] as the root doesn't have any parents
  options: StringifyOptions
) => {
  if (branches.length === 0) return '';
  const { multi, color } = options;
  const fmt = chalk[color];
  let stringified = '';

  for (let i = 0; i < branches.length; i++) {
    const isLastNode = i === branches.length - 1;
    const node = branches[i]!;
    const isActivePath = activePath === node.fullPath;
    const isSelectedPath = selectedPaths.includes(node.fullPath);
    const indicator = multi ? (isSelectedPath ? '(*) ' : '( ) ') : isActivePath ? '\u25CF ' : '';
    const formattedIndicator = isActivePath ? fmt(indicator) : indicator;

    const line = [
      LF,
      ...parents.map(p => (p === 0 ? PBR : SP)),
      isLastNode ? BRE : BR,
      formattedIndicator,
      isActivePath ? fmt(node.name) : node.name,
      isActivePath ? fmt(node.dir ? '/' : '') : node.dir ? '/' : '',
    ]
      .flat()
      .join('');

    const newParents = [...parents, isLastNode ? 1 : 0];

    stringified += line;
    stringified += stringifyBranches(
      node.branches,
      activePath,
      selectedPaths,
      level + 1, // * next level
      newParents,
      options
    );
  }

  return stringified;
};

export const stringifyTree = (
  tree: Tree,
  activePath: string,
  selectedPaths: string[],
  options: Partial<StringifyOptions>
) => {
  options.multi = options.multi ?? false;
  options.color = options.color ?? 'blue';

  return (
    tree.name +
    stringifyBranches(tree.branches, activePath, selectedPaths, 0, [], options as StringifyOptions)
  );
};

export const flattenTree = (tree: Tree): string[] => {
  return [tree.fullPath, ...tree.branches.flatMap(flattenTree)];
};
